import { motion, useReducedMotion } from 'framer-motion'
import { Seo } from '@/components/seo/Seo'
import { InstagramIcon } from '@/components/icons/SocialIcons'
import { site } from '@/content/site'
import { smoothEase } from '@/lib/motion'

const HERO_IMAGE = '/interior/2.jpg'

export default function ComingSoonPage() {
  const reduced = useReducedMotion()

  return (
    <>
      <Seo
        title={`${site.name} | Coming Soon · ${site.city}`}
        description={`Rodeo Nails is opening soon in ${site.city}, ${site.region}. Luxury manicures, pedicures, and nail care — stay tuned.`}
        path="/"
        keywords={site.seoKeywords}
      />

      <main className="relative min-h-svh overflow-hidden bg-olive text-on-olive">
        <div className="absolute inset-0" aria-hidden>
          <img
            src={HERO_IMAGE}
            alt=""
            className="h-full w-full object-cover object-[58%_center]"
            width={1200}
            height={1680}
            decoding="async"
            fetchPriority="high"
          />
          <div
            className="absolute inset-0"
            style={{
              background: [
                'linear-gradient(105deg, color-mix(in srgb, var(--color-olive) 78%, transparent) 0%, color-mix(in srgb, var(--color-olive) 48%, transparent) 38%, color-mix(in srgb, var(--color-olive) 18%, transparent) 100%)',
                'linear-gradient(to top, color-mix(in srgb, var(--color-olive) 55%, transparent) 0%, transparent 28%)',
              ].join(', '),
            }}
          />
        </div>

        <div className="relative z-10 flex min-h-svh flex-col">
          <header className="px-6 pt-[max(1.5rem,env(safe-area-inset-top))] sm:px-10 md:px-14">
            <motion.p
              className="font-brand-display text-[clamp(2.4rem,7vw,4.25rem)] leading-none tracking-[0.02em] text-on-olive"
              initial={reduced ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: reduced ? 0 : 0.7, ease: smoothEase }}
            >
              {site.name}
            </motion.p>
          </header>

          <div className="flex flex-1 flex-col justify-center px-6 py-16 sm:px-10 md:px-14">
            <div className="max-w-xl">
              <motion.p
                className="text-[11px] font-semibold uppercase tracking-[0.28em] text-on-olive-muted"
                initial={reduced ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: reduced ? 0 : 0.55,
                  delay: reduced ? 0 : 0.08,
                  ease: smoothEase,
                }}
              >
                Opening soon · {site.city}
              </motion.p>

              <motion.h1
                className="mt-5 font-display text-[clamp(2.35rem,6.5vw,3.75rem)] font-semibold leading-[1.05] tracking-tight text-on-olive"
                initial={reduced ? false : { opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: reduced ? 0 : 0.65,
                  delay: reduced ? 0 : 0.14,
                  ease: smoothEase,
                }}
              >
                Something beautiful is on the way
              </motion.h1>

              <motion.p
                className="mt-5 max-w-md text-base leading-relaxed text-on-olive-soft sm:text-lg"
                initial={reduced ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: reduced ? 0 : 0.6,
                  delay: reduced ? 0 : 0.22,
                  ease: smoothEase,
                }}
              >
                A quiet, carefully designed nail studio is coming to{' '}
                {site.city}. Online booking will open here when we do.
              </motion.p>

              <motion.div
                className="mt-10"
                initial={reduced ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: reduced ? 0 : 0.55,
                  delay: reduced ? 0 : 0.3,
                  ease: smoothEase,
                }}
              >
                <a
                  href={site.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center gap-2 rounded-[var(--radius-button)] bg-on-olive px-6 text-sm font-semibold tracking-wide text-olive transition-[transform,opacity] duration-200 hover:opacity-95 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-olive"
                >
                  <InstagramIcon size={18} />
                  Follow along
                </a>
              </motion.div>
            </div>
          </div>

          <footer className="border-t border-olive-border/60 px-6 py-6 sm:px-10 md:px-14">
            <motion.div
              className="flex flex-col gap-2 text-sm text-on-olive-muted sm:flex-row sm:items-center sm:justify-between sm:gap-6"
              initial={reduced ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: reduced ? 0 : 0.5,
                delay: reduced ? 0 : 0.4,
                ease: smoothEase,
              }}
            >
              <p>{site.formattedAddress}</p>
              <a
                href={`mailto:${site.email}`}
                className="text-on-olive-soft transition-colors hover:text-on-olive focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-olive"
              >
                {site.email}
              </a>
            </motion.div>
          </footer>
        </div>
      </main>
    </>
  )
}
